// Node-shaped environment type kept local to this module: the library targets the browser and pulls
// in no Node types. The `process.env.*` accesses below must stay literal member expressions —
// bundlers substitute them textually (Next.js via the `env` block in its next.config), so reading
// them through a local alias would defeat that substitution and leave the values undefined.
declare const process: {
    readonly env: {
        readonly NODE_ENV?: string
        readonly IS_TEST_BUILD?: string
    }
}

/**
 * Absent in the Vite SPAs (`trustoo_pros`, `office`), which never define a `process` global, so
 * every constant below reads as production there — those apps should use {@link IS_TEST} instead.
 */
const HAS_PROCESS = typeof process !== 'undefined'

/** The strings a build script may set a boolean environment variable to. */
const TRUE_ENV_VALUES = ['1', 'true']

/** Local development: `next dev`, or a build run with `NODE_ENV=development`. */
export const IS_DEVELOPMENT_BUILD = HAS_PROCESS && process.env.NODE_ENV === 'development'

/**
 * A bundle built for a test deployment, which the app's `build_test`-style script sets — in CI that
 * is `DEPLOY_MODE=dev`, i.e. a branch that does not target `main`. Only the values in
 * {@link TRUE_ENV_VALUES} count, so `0` and `false` read as production rather than as "the variable
 * is present".
 */
export const IS_TEST_BUILD =
    HAS_PROCESS && TRUE_ENV_VALUES.includes(process.env.IS_TEST_BUILD ?? '')

/**
 * Non-production signal derived from build-time environment variables, so it stays valid while
 * server rendering. {@link IS_TEST} includes it, and adds host checks that need `window.location`.
 *
 * @see IS_TEST in `@trustoo/constants/environment`
 */
export const IS_TEST_ENV = IS_DEVELOPMENT_BUILD || IS_TEST_BUILD
